import React, { useState, useEffect, useMemo, useRef } from 'react';
import { marketplaceApi } from '../api/marketplace';
import { authApi } from '../api/auth';
import { usersApi } from '../api/users';
import { cardsApi } from '../api/cards';
import { Search, Filter, Gavel, ShoppingBag, Package, Clock, ChevronLeft, ChevronRight, ArrowLeftRight, RotateCcw } from 'lucide-react';
import AuctionCard from './marketplace/AuctionCard';
import SaleCard from './marketplace/SaleCard';
import PackCard from './marketplace/PackCard';
import LoanCard from './marketplace/LoanCard';
import TradeCard from './marketplace/TradeCard';
import '../styles/football-theme.css';
import '../styles/marketplace-gaming.css';
import { formatD10S } from '../utils/formatters';
import { useLanguage } from '../i18n/LanguageContext';
import { syncBalanceFromProfile } from '../hooks/useBalance';

/** Player account id for marketplace (admin may play via linkedUserId). */
const resolvePlayerId = (u) => {
  if (!u) return null;
  if (u.isAdmin) return u.linkedUserId ?? u.userId ?? null;
  return u.id;
};

const Marketplace = () => {
  const [activeTab, setActiveTab] = useState('auctions'); // auctions, packs, sales, loans, trades
  const [auctions, setAuctions] = useState([]);
  const [sales, setSales] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loans, setLoans] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(authApi.getCurrentUser());
  const [message, setMessage] = useState({ type: '', text: '' });
  const [auctionNow, setAuctionNow] = useState(Date.now());
  const [auctionEndTimes, setAuctionEndTimes] = useState([]);
  const auctionTimerRef = useRef(null);
  const INITIAL_FILTERS = {
    search: '',
    rarity: '',
    position: '',
    nationality: '',
    team: '',
    league: '',
    ageMin: '',
    ageMax: '',
    xpMin: '',
    xpMax: '',
    auctionStatus: 'active'
  };
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Pagination state
  const [pageAuctions, setPageAuctions] = useState(0);
  const [pageSales, setPageSales] = useState(0);
  const [pagePacks, setPagePacks] = useState(0);
  const [pageLoans, setPageLoans] = useState(0);
  const [pageTrades, setPageTrades] = useState(0);
  const [totalAuctions, setTotalAuctions] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [totalPacks, setTotalPacks] = useState(0);
  const [totalLoans, setTotalLoans] = useState(0);
  const [totalTrades, setTotalTrades] = useState(0);
  const ITEMS_PER_PAGE = 6;
  const [secondaryMarketDivinityCoachOnly, setSecondaryMarketDivinityCoachOnly] = useState(false);
  const { t } = useLanguage();

  const SECONDARY_MARKET_TABS = ['sales', 'loans', 'trades', 'auctions'];
  const showSecondaryMarketFilterNotice =
    secondaryMarketDivinityCoachOnly && SECONDARY_MARKET_TABS.includes(activeTab);

  useEffect(() => {
    marketplaceApi.getSettings()
      .then((res) => setSecondaryMarketDivinityCoachOnly(!!res?.divinityCoachOnly))
      .catch(() => setSecondaryMarketDivinityCoachOnly(false));
  }, []);

  useEffect(() => {
    // Reset pagination when switching tabs or filters
    setPageAuctions(0);
    setPageSales(0);
    setPagePacks(0);
    setPageLoans(0);
    setPageTrades(0);
    loadData();
    loadUser();
    // Real-time polling (see effect below) handles balance and list refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  useEffect(() => {
    if (auctionTimerRef.current) {
      clearTimeout(auctionTimerRef.current);
      auctionTimerRef.current = null;
    }

    if (activeTab !== 'auctions' || filters.auctionStatus === 'closed' || auctionEndTimes.length === 0) {
      return;
    }

    const now = Date.now();
    const nextEnd = auctionEndTimes
      .filter((endTime) => endTime > now)
      .sort((a, b) => a - b)[0];

    if (!nextEnd) {
      return;
    }

    const delay = Math.max(0, nextEnd - now);
    auctionTimerRef.current = setTimeout(() => {
      setAuctionNow(Date.now());
    }, delay);

    return () => {
      if (auctionTimerRef.current) {
        clearTimeout(auctionTimerRef.current);
        auctionTimerRef.current = null;
      }
    };
  }, [activeTab, auctionEndTimes, auctionNow, filters.auctionStatus]);

  useEffect(() => {
    if (activeTab !== 'auctions') return;
    const endTimes = auctions
      .map((a) => new Date(a.end_time).getTime())
      .filter((t) => Number.isFinite(t));
    setAuctionEndTimes(endTimes);
  }, [auctions, activeTab]);

  const loadUser = async () => {
    const currentUser = authApi.getCurrentUser();
    const playerId = resolvePlayerId(currentUser);
    if (!playerId) {
      if (currentUser) setUser(currentUser);
      return;
    }
    try {
      const [userResponse, balanceResponse] = await Promise.all([
        usersApi.getById(playerId),
        !currentUser?.isAdmin ? marketplaceApi.getBalances().catch(() => null) : Promise.resolve(null)
      ]);

      if (userResponse?.success && userResponse.user) {
        const apiUser = userResponse.user;
        let d10s = apiUser.d10s_balance ?? apiUser.d10sBalance;
        let bonus = parseFloat(apiUser.bonus_balance ?? apiUser.bonusBalance ?? 0) || 0;

        if (balanceResponse?.success) {
          if (balanceResponse.balances) {
            d10s = balanceResponse.balances.d10s ?? d10s;
            bonus = parseFloat(balanceResponse.balances.bonus) || bonus;
          } else if (balanceResponse.balance) {
            d10s = balanceResponse.balance.d10s ?? d10s;
            bonus = parseFloat(balanceResponse.balance.bonus) || bonus;
          }
        }

        const merged = {
          ...currentUser,
          ...apiUser,
          id: apiUser.id ?? playerId,
          d10s_balance: d10s,
          d10sBalance: d10s,
          bonus_balance: bonus,
          bonusBalance: bonus
        };
        setUser(merged);
        if (!currentUser?.isAdmin) {
          localStorage.setItem('user', JSON.stringify(merged));
        }
        if (d10s != null && d10s !== '') {
          syncBalanceFromProfile(d10s);
        }
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (activeTab === 'auctions') {
        const response = await marketplaceApi.getAuctions({
          ...filters,
          status: filters.auctionStatus
        });
        if (response?.success) {
          const fetched = response.auctions || [];
          setAuctions((prev) => {
            if (!prev.length) return fetched;
            return fetched.map((f) => {
              const p = prev.find((x) => Number(x.id) === Number(f.id));
              if (!p) return f;
              const fEnd = new Date(f.end_time).getTime();
              const pEnd = new Date(p.end_time).getTime();
              if (Number.isFinite(pEnd) && Number.isFinite(fEnd) && pEnd > fEnd + 500) {
                return { ...f, end_time: p.end_time };
              }
              return f;
            });
          });
          setTotalAuctions(fetched.length);
        } else if (response?.error && !silent) {
          console.error('Error loading auctions:', response.error);
          setAuctions([]);
          setTotalAuctions(0);
          setAuctionEndTimes([]);
        }
      } else if (activeTab === 'sales') {
        const response = await marketplaceApi.getSales({ ...filters });
        if (response?.success) {
          const allSales = response.sales || [];
          setSales(allSales);
          setTotalSales(allSales.length);
        } else if (response?.error && !silent) {
          console.error('Error loading sales:', response.error);
          setSales([]);
          setTotalSales(0);
        }
      } else if (activeTab === 'packs') {
        const response = await marketplaceApi.getPacks();
        if (response?.success) {
          const allPacks = response.packs || [];
          const start = pagePacks * ITEMS_PER_PAGE;
          setPacks(allPacks.slice(start, start + ITEMS_PER_PAGE));
          setTotalPacks(allPacks.length);
        } else if (response?.error && !silent) {
          console.error('Error loading packs:', response.error);
          setPacks([]);
          setTotalPacks(0);
        }
      } else if (activeTab === 'loans') {
        const response = await marketplaceApi.getLoans({ ...filters });
        if (response?.success) {
          const allLoans = response.loans || [];
          setLoans(allLoans);
          setTotalLoans(allLoans.length);
        } else if (response?.error && !silent) {
          console.error('Error loading loans:', response.error);
          setLoans([]);
          setTotalLoans(0);
        }
      } else if (activeTab === 'trades') {
        // Show all open trades, but exclude trades created by the current user
        try {
          const response = await marketplaceApi.getOpenTrades(100);
          console.log('[Marketplace] Open trades response:', response);
          
          // Check if response has trades array (even if empty)
          if (response && Array.isArray(response.trades)) {
            let tradesArray = response.trades;
            
            // Filter out trades created by the current user
            if (user?.id) {
              tradesArray = tradesArray.filter(trade => trade.user1Id !== user.id);
              console.log('[Marketplace] Filtered out user own trades. Remaining:', tradesArray.length);
            }
            
            console.log('[Marketplace] Found trades:', tradesArray.length, tradesArray);
            
            const start = pageTrades * ITEMS_PER_PAGE;
            setTrades(tradesArray.slice(start, start + ITEMS_PER_PAGE));
            setTotalTrades(tradesArray.length);
          } else {
            console.warn('[Marketplace] Invalid response format:', response);
            setTrades([]);
            setTotalTrades(0);
          }
        } catch (error) {
          console.error('[Marketplace] Error loading open trades:', error);
          setTrades([]);
          setTotalTrades(0);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      if (!silent) {
        setAuctions([]);
        setSales([]);
        setPacks([]);
        setLoans([]);
        setTrades([]);
        setTotalAuctions(0);
        setTotalSales(0);
        setTotalPacks(0);
        setTotalLoans(0);
        setTotalTrades(0);
        setAuctionEndTimes([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Real-time polling: refresh data without full page reload (no loading spinner)
  const POLL_AUCTIONS_MS = 2800;
  const POLL_OTHER_MS = 15000;
  const loadDataRef = useRef(loadData);
  const loadUserRef = useRef(loadUser);
  loadDataRef.current = loadData;
  loadUserRef.current = loadUser;
  useEffect(() => {
    loadUserRef.current();
    const balanceInterval = setInterval(() => loadUserRef.current(), 10000);
    return () => clearInterval(balanceInterval);
  }, []);

  useEffect(() => {
    if (activeTab !== 'auctions' && activeTab !== 'sales' && activeTab !== 'loans' && activeTab !== 'trades') return;
    const intervalMs = activeTab === 'auctions' && filters.auctionStatus === 'active' ? POLL_AUCTIONS_MS : POLL_OTHER_MS;
    const interval = setInterval(() => {
      loadDataRef.current(true);
      loadUserRef.current();
    }, intervalMs);
    return () => clearInterval(interval);
  }, [activeTab, filters.auctionStatus]);

  // Mobile / background tabs: refresh auctions when user returns (polling is often throttled)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (activeTab === 'auctions' && filters.auctionStatus === 'active') {
        loadDataRef.current(true);
        loadUserRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [activeTab, filters.auctionStatus]);

  const visibleAuctions = useMemo(() => {
    if (!auctions.length) return auctions;
    return auctions.filter((auction) => {
      const endTime = new Date(auction.end_time).getTime();
      if (!Number.isFinite(endTime)) return true;
      if (filters.auctionStatus === 'closed') {
        return endTime <= auctionNow;
      }
      return endTime > auctionNow;
    });
  }, [auctions, auctionNow, filters.auctionStatus]);

  const pagedAuctions = useMemo(() => {
    const start = pageAuctions * ITEMS_PER_PAGE;
    return visibleAuctions.slice(start, start + ITEMS_PER_PAGE);
  }, [visibleAuctions, pageAuctions]);

  const pagedSales = useMemo(() => {
    const start = pageSales * ITEMS_PER_PAGE;
    return sales.slice(start, start + ITEMS_PER_PAGE);
  }, [sales, pageSales]);

  const pagedLoans = useMemo(() => {
    const start = pageLoans * ITEMS_PER_PAGE;
    return loans.slice(start, start + ITEMS_PER_PAGE);
  }, [loans, pageLoans]);

  useEffect(() => {
    if (activeTab === 'auctions') {
      setTotalAuctions(visibleAuctions.length);
    }
  }, [activeTab, visibleAuctions.length]);

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPageAuctions(0);
    setPageSales(0);
    setPagePacks(0);
    setPageLoans(0);
    setPageTrades(0);
  };

  const tabs = [
    { id: 'auctions', name: t('marketplace.tabsAuctions'), icon: Gavel },
    { id: 'packs', name: t('marketplace.tabsPacks'), icon: Package },
    { id: 'sales', name: t('marketplace.tabsSales'), icon: ShoppingBag },
    { id: 'loans', name: t('marketplace.tabsLoans'), icon: ShoppingBag },
    { id: 'trades', name: t('marketplace.tabsTrades'), icon: ArrowLeftRight },
  ];

  return (
    <div className="marketplace-container min-h-screen pt-20 pb-8 px-4 sm:px-6 lg:px-8">
      {/* Marketplace Background */}
      <div 
        className="marketplace-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/marketplacebg.png')`
        }}
      ></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-[#1e293b]">{t('marketplace.title')}</h1>
          {user && (
            <div className="flex flex-wrap gap-3">
              <div className="football-card glass-football px-6 py-3">
                <div className="text-[#64748b] text-sm">{t('marketplace.balanceLabel')}</div>
                <div className="text-[#4169E1] font-bold text-xl">{formatD10S(user.d10s_balance || 0)} DIOS</div>
              </div>
              <div className="football-card glass-football px-6 py-3 border border-amber-400/40">
                <div className="text-[#64748b] text-sm">Bonus</div>
                <div className="text-amber-600 font-bold text-xl">
                  {formatD10S(user.bonus_balance ?? user.bonusBalance ?? 0)} Bonus
                </div>
                <div className="text-[#64748b] text-xs mt-0.5">Solo aste e pack · non prelevabile</div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={tab.id === 'trades' ? { display: 'none' } : undefined}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#4169E1] text-white'
                    : 'bg-white text-[#64748b] hover:bg-[rgba(65,105,225,0.1)] border border-[rgba(65,105,225,0.2)]'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="football-card glass-football p-6 mb-6">
          <div className="mobile-filters-header mb-6">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-[#4169E1]" />
              <h2 className="text-xl font-bold text-[#1e293b]">{t('marketplace.filtersTitle')}</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-[#4169E1] bg-[rgba(65,105,225,0.1)] border border-[rgba(65,105,225,0.3)] hover:bg-[rgba(65,105,225,0.2)] transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                {t('marketplace.resetFilters')}
              </button>
              <button
                type="button"
                onClick={() => setFiltersOpen((prev) => !prev)}
                className="mobile-filters-toggle"
              >
                {filtersOpen ? 'Nascondi' : 'Mostra'}
              </button>
            </div>
          </div>
          <div className={`mobile-filters-body ${filtersOpen ? 'is-open' : 'is-closed'}`}>
            {activeTab === 'auctions' && (
              <div className="auction-status-section mb-6">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, auctionStatus: 'active' })}
                    className={`auction-status-btn ${filters.auctionStatus === 'active' ? 'auction-status-btn--active' : ''}`}
                  >
                    {t('marketplace.auctionStatusOpen')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, auctionStatus: 'closed' })}
                    className={`auction-status-btn ${filters.auctionStatus === 'closed' ? 'auction-status-btn--active' : ''}`}
                  >
                    {t('marketplace.auctionStatusClosed')}
                  </button>
                </div>
                {filters.auctionStatus === 'closed' && (
                  <div className="auction-status-hint mt-2">
                    {t('marketplace.auctionStatusClosedHint')}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
              <div className="md:col-span-2 lg:col-span-2">
                <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                  {t('marketplace.searchPlayerLabel')}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#64748b]" />
                  <input
                    type="text"
                    placeholder={t('marketplace.searchPlayerPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="input-football marketplace-search-input w-full"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                  {t('marketplace.rarityLabel')}
                </label>
                <select
                  value={filters.rarity}
                  onChange={(e) => setFilters({ ...filters, rarity: e.target.value })}
                  className="w-full p-3 rounded-lg border-2 border-[rgba(65,105,225,0.3)] bg-white text-[#1e293b] font-medium focus:border-[#4169E1] focus:outline-none focus:ring-2 focus:ring-[#4169E1]/50 transition-all appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%231e293b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                    paddingRight: '2.5rem'
                  }}
                >
                  <option value="">{t('marketplace.allRarities')}</option>
                  <option value="basic">⭐ {t('marketplace.rarityBasic')}</option>
                  <option value="plus">⭐ {t('marketplace.rarityPlus')}</option>
                  <option value="rare">⭐ {t('marketplace.rarityRare')}</option>
                  <option value="epic">⭐ {t('marketplace.rarityEpic')}</option>
                  <option value="unique">⭐ {t('marketplace.rarityUnique')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                  {t('marketplace.positionLabel')}
                </label>
                <select
                  value={filters.position}
                  onChange={(e) => setFilters({ ...filters, position: e.target.value })}
                  className="w-full p-3 rounded-lg border-2 border-[rgba(65,105,225,0.3)] bg-white text-[#1e293b] font-medium focus:border-[#4169E1] focus:outline-none focus:ring-2 focus:ring-[#4169E1]/50 transition-all appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%231e293b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                    paddingRight: '2.5rem'
                  }}
                >
                  <option value="">{t('marketplace.allPositions')}</option>
                  <option value="GK">🥅 {t('marketplace.positionGK')}</option>
                  <option value="DEF">🛡️ {t('marketplace.positionDEF')}</option>
                  <option value="MID">⚙️ {t('marketplace.positionMID')}</option>
                  <option value="ATT">⚽ {t('marketplace.positionATT')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                  {t('marketplace.nationalityLabel')}
                </label>
              <input
                type="text"
                placeholder={t('marketplace.nationalityPlaceholder')}
                value={filters.nationality}
                onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
                className="input-football w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                {t('marketplace.teamLabel')}
              </label>
              <input
                type="text"
                placeholder={t('marketplace.teamPlaceholder')}
                value={filters.team}
                onChange={(e) => setFilters({ ...filters, team: e.target.value })}
                className="input-football w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                {t('marketplace.leagueLabel')}
              </label>
              <input
                type="text"
                placeholder={t('marketplace.leaguePlaceholder')}
                value={filters.league}
                onChange={(e) => setFilters({ ...filters, league: e.target.value })}
                className="input-football w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                {t('marketplace.ageMinLabel')}
              </label>
              <input
                type="number"
                min="0"
                placeholder={t('marketplace.ageMinPlaceholder')}
                value={filters.ageMin}
                onChange={(e) => setFilters({ ...filters, ageMin: e.target.value })}
                className="input-football w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                {t('marketplace.ageMaxLabel')}
              </label>
              <input
                type="number"
                min="0"
                placeholder={t('marketplace.ageMaxPlaceholder')}
                value={filters.ageMax}
                onChange={(e) => setFilters({ ...filters, ageMax: e.target.value })}
                className="input-football w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                {t('marketplace.xpMinLabel')}
              </label>
              <input
                type="number"
                min="0"
                placeholder={t('marketplace.xpMinPlaceholder')}
                value={filters.xpMin}
                onChange={(e) => setFilters({ ...filters, xpMin: e.target.value })}
                className="input-football w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1e293b] mb-2">
                {t('marketplace.xpMaxLabel')}
              </label>
              <input
                type="number"
                min="0"
                placeholder={t('marketplace.xpMaxPlaceholder')}
                value={filters.xpMax}
                onChange={(e) => setFilters({ ...filters, xpMax: e.target.value })}
                className="input-football w-full"
              />
            </div>
          </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="football-spinner mx-auto"></div>
            <p className="mt-4 text-[#64748b]">{t('common.loading')}</p>
          </div>
        ) : (
          <>
            {showSecondaryMarketFilterNotice && (
              <div className="football-card glass-football px-4 py-3 mb-4 border border-amber-400/40">
                <div className="text-[#1e293b] text-sm">
                  {t('marketplace.secondaryMarketFilterNotice')}
                </div>
              </div>
            )}
            {activeTab === 'auctions' && (
              <div className="football-card glass-football px-4 py-3 mb-4">
                <div className="text-[#1e293b] text-sm font-semibold">{t('marketplace.auctionsInfoTitle')}</div>
                <div className="text-[#64748b] text-sm">
                  {t('marketplace.auctionsInfoBody')}
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeTab === 'auctions' && pagedAuctions.map((auction) => (
                <AuctionCard 
                  key={auction.id} 
                  auction={auction} 
                  user={user}
                  onBidSuccess={(bidResponse) => {
                    setMessage({ type: 'success', text: t('marketplace.bidSuccess') });
                    const bidPayload = bidResponse?.bid ?? bidResponse;
                    if (bidPayload?.auctionId != null && bidPayload?.bidAmount != null) {
                      const aid = Number(bidPayload.auctionId);
                      const newEndRaw = bidPayload.newEndTime;
                      const newEndIso =
                        newEndRaw != null
                          ? typeof newEndRaw === 'string'
                            ? newEndRaw
                            : new Date(newEndRaw).toISOString()
                          : null;
                      setAuctions((prev) =>
                        prev.map((a) =>
                          Number(a.id) === aid
                            ? {
                                ...a,
                                current_price: String(bidPayload.bidAmount),
                                end_time: newEndIso ?? a.end_time,
                                current_bidder: user ? { id: user.id, username: user.username || '' } : { id: bidPayload.bidderId, username: '' }
                              }
                            : a
                        )
                      );
                    }
                    loadData(true);
                    loadUser();
                  }}
                  onError={(error) => setMessage({ type: 'error', text: `❌ ${error}` })}
                  onBidAuctionUpdated={() => loadData(true)}
                />
              ))}
              {activeTab === 'sales' && pagedSales.map((sale) => (
                <SaleCard 
                  key={sale.id} 
                  sale={sale}
                  user={user}
                  onPurchaseSuccess={() => {
                    setMessage({ type: 'success', text: t('marketplace.purchaseSuccess') });
                    loadData();
                    loadUser(); // Refresh user balance
                  }}
                  onError={(error) => setMessage({ type: 'error', text: `❌ ${error}` })}
                />
              ))}
              {activeTab === 'packs' && packs.map((pack) => (
                <PackCard 
                  key={pack.id} 
                  pack={pack}
                  user={user}
                  onOpenSuccess={() => {
                    setMessage({ type: 'success', text: t('marketplace.packSuccess') });
                    loadData();
                    loadUser(); // Refresh user balance
                  }}
                  onError={(error) => setMessage({ type: 'error', text: `❌ ${error}` })}
                />
              ))}
              {activeTab === 'loans' && pagedLoans.map((loan) => (
                <LoanCard 
                  key={loan.id} 
                  loan={loan}
                  user={user}
                  onAcceptSuccess={() => {
                    setMessage({ type: 'success', text: t('marketplace.loanSuccess') });
                    loadData();
                    loadUser(); // Refresh user balance
                  }}
                  onError={(error) => setMessage({ type: 'error', text: `❌ ${error}` })}
                />
              ))}
              {activeTab === 'trades' && trades.map((trade) => (
                <TradeCard 
                  key={trade.id} 
                  trade={trade}
                  user={user}
                  onAcceptSuccess={() => {
                    setMessage({ type: 'success', text: t('marketplace.tradeAcceptSuccess') });
                    loadData();
                    loadUser();
                  }}
                  onRejectSuccess={() => {
                    setMessage({ type: 'success', text: t('marketplace.tradeRejectSuccess') });
                    loadData();
                  }}
                  onError={(error) => setMessage({ type: 'error', text: `❌ ${error}` })}
                  onCounterOfferSuccess={() => {
                    setMessage({ type: 'success', text: t('marketplace.counterOfferSuccess') });
                    loadData();
                  }}
                />
              ))}
            </div>
            
            {/* Pagination Controls */}
            {activeTab === 'auctions' && totalAuctions > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={() => setPageAuctions(Math.max(0, pageAuctions - 1))}
                  disabled={pageAuctions === 0}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                    pageAuctions === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#4169E1] text-white hover:bg-[#3155C7]'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  {t('common.previous')}
                </button>
                <span className="marketplace-pagination-text text-sm font-medium">
                  {t('common.pageOf', { page: pageAuctions + 1, total: Math.ceil(totalAuctions / ITEMS_PER_PAGE) })}
                </span>
                <button
                  onClick={() => setPageAuctions(Math.min(Math.ceil(totalAuctions / ITEMS_PER_PAGE) - 1, pageAuctions + 1))}
                  disabled={pageAuctions >= Math.ceil(totalAuctions / ITEMS_PER_PAGE) - 1}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                    pageAuctions >= Math.ceil(totalAuctions / ITEMS_PER_PAGE) - 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#4169E1] text-white hover:bg-[#3155C7]'
                  }`}
                >
                  {t('common.next')}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </button>
              </div>
            )}
            {activeTab === 'sales' && totalSales > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={() => setPageSales(Math.max(0, pageSales - 1))}
                  disabled={pageSales === 0}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                    pageSales === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#4169E1] text-white hover:bg-[#3155C7]'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  {t('common.previous')}
                </button>
                <span className="marketplace-pagination-text text-sm font-medium">
                  {t('common.pageOf', { page: pageSales + 1, total: Math.ceil(totalSales / ITEMS_PER_PAGE) })}
                </span>
                <button
                  onClick={() => setPageSales(Math.min(Math.ceil(totalSales / ITEMS_PER_PAGE) - 1, pageSales + 1))}
                  disabled={pageSales >= Math.ceil(totalSales / ITEMS_PER_PAGE) - 1}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                    pageSales >= Math.ceil(totalSales / ITEMS_PER_PAGE) - 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#4169E1] text-white hover:bg-[#3155C7]'
                  }`}
                >
                  {t('common.next')}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </button>
              </div>
            )}
            {activeTab === 'packs' && totalPacks > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={() => setPagePacks(Math.max(0, pagePacks - 1))}
                  disabled={pagePacks === 0}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                    pagePacks === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#4169E1] text-white hover:bg-[#3155C7]'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  {t('common.previous')}
                </button>
                <span className="marketplace-pagination-text text-sm font-medium">
                  {t('common.pageOf', { page: pagePacks + 1, total: Math.ceil(totalPacks / ITEMS_PER_PAGE) })}
                </span>
                <button
                  onClick={() => setPagePacks(Math.min(Math.ceil(totalPacks / ITEMS_PER_PAGE) - 1, pagePacks + 1))}
                  disabled={pagePacks >= Math.ceil(totalPacks / ITEMS_PER_PAGE) - 1}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                    pagePacks >= Math.ceil(totalPacks / ITEMS_PER_PAGE) - 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#4169E1] text-white hover:bg-[#3155C7]'
                  }`}
                >
                  {t('common.next')}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </button>
              </div>
            )}
            {activeTab === 'loans' && totalLoans > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={() => setPageLoans(Math.max(0, pageLoans - 1))}
                  disabled={pageLoans === 0}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                    pageLoans === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#4169E1] text-white hover:bg-[#3155C7]'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  {t('common.previous')}
                </button>
                <span className="marketplace-pagination-text text-sm font-medium">
                  {t('common.pageOf', { page: pageLoans + 1, total: Math.ceil(totalLoans / ITEMS_PER_PAGE) })}
                </span>
                <button
                  onClick={() => setPageLoans(Math.min(Math.ceil(totalLoans / ITEMS_PER_PAGE) - 1, pageLoans + 1))}
                  disabled={pageLoans >= Math.ceil(totalLoans / ITEMS_PER_PAGE) - 1}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                    pageLoans >= Math.ceil(totalLoans / ITEMS_PER_PAGE) - 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#4169E1] text-white hover:bg-[#3155C7]'
                  }`}
                >
                  {t('common.next')}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </button>
              </div>
            )}
          </>
        )}

        {!loading && (
          <>
            {activeTab === 'auctions' && visibleAuctions.length === 0 && (
              <div className="marketplace-empty-state text-center py-12">
                <div className="marketplace-empty-icon text-4xl mb-4">🎯</div>
                <div className="marketplace-empty-title text-xl font-semibold text-[#1e293b] mb-2">{t('marketplace.emptyAuctionsTitle')}</div>
                <div className="marketplace-empty-body text-[#64748b]">{t('marketplace.emptyAuctionsBody')}</div>
              </div>
            )}
            {activeTab === 'sales' && totalSales === 0 && (
              <div className="marketplace-empty-state text-center py-12">
                <div className="marketplace-empty-icon text-4xl mb-4">🛒</div>
                <div className="marketplace-empty-title text-xl font-semibold text-[#1e293b] mb-2">{t('marketplace.emptySalesTitle')}</div>
                <div className="marketplace-empty-body text-[#64748b]">{t('marketplace.emptySalesBody')}</div>
              </div>
            )}
            {activeTab === 'packs' && packs.length === 0 && (
              <div className="marketplace-empty-state text-center py-12">
                <div className="marketplace-empty-icon text-4xl mb-4">📦</div>
                <div className="marketplace-empty-title text-xl font-semibold text-[#1e293b] mb-2">{t('marketplace.emptyPacksTitle')}</div>
                <div className="marketplace-empty-body text-[#64748b]">{t('marketplace.emptyPacksBody')}</div>
              </div>
            )}
            {activeTab === 'loans' && totalLoans === 0 && (
              <div className="marketplace-empty-state text-center py-12">
                <div className="marketplace-empty-icon text-4xl mb-4">🔄</div>
                <div className="marketplace-empty-title text-xl font-semibold text-[#1e293b] mb-2">{t('marketplace.emptyLoansTitle')}</div>
                <div className="marketplace-empty-body text-[#64748b]">{t('marketplace.emptyLoansBody')}</div>
              </div>
            )}
            {activeTab === 'trades' && trades.length === 0 && (
              <div className="marketplace-empty-state text-center py-12">
                <div className="marketplace-empty-icon text-4xl mb-4">🔄</div>
                <div className="marketplace-empty-title text-xl font-semibold text-[#1e293b] mb-2">{t('marketplace.emptyTradesTitle')}</div>
                <div className="marketplace-empty-body text-[#64748b]">{t('marketplace.emptyTradesBody')}</div>
              </div>
            )}
          </>
        )}

        {/* Message display - in basso su mobile (evita navbar), in alto su desktop */}
        {message.text && (
          <div className={`app-toast fixed z-[90] bottom-4 left-4 right-4 sm:bottom-auto sm:top-24 sm:left-auto sm:right-4 p-4 rounded-lg shadow-xl backdrop-blur-md border-2 flex items-center justify-between gap-3 ${
            message.type === 'success'
              ? 'bg-green-600/85 border-green-400/80'
              : message.type === 'warning'
                ? 'bg-amber-500/85 border-amber-400/80 text-white'
                : 'bg-red-600/85 border-red-400/80 text-white'
          }`}>
            <span
              className={
                message.type === 'success'
                  ? 'flex-1 font-medium platform-notification-text'
                  : 'flex-1 font-medium text-white'
              }
            >
              {message.text}
            </span>
            <button
              type="button"
              onClick={() => setMessage({ type: '', text: '' })}
              className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-white/50 bg-white/20 hover:bg-white/30 text-white flex items-center justify-center text-lg leading-none transition-colors"
              aria-label="Chiudi"
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;
